<template> 
  <div class="login-container pt-5 mt-5">
    <div class="login-content mt-5">
      <div class="login-box">
        <!-- Logo/Título -->
        <div class="logo-container">
          <h1 class="welcome-text">¡Bienvenido!</h1>
          <p class="text-muted">Inicia sesión para continuar</p>
        </div>

        <form v-if="!showVerification" @submit.prevent="handleSubmit" class="needs-validation w-100" novalidate>
          <!-- Campo Usuario -->
          <div class="form-group">
            <div class="form-floating">
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.username.$error }"
                id="username"
                v-model="formData.username"
                placeholder="Usuario"
                required
              >
              <label for="username">
                <i class="fas fa-user text-muted me-2"></i>Usuario
              </label>
              <div class="invalid-feedback" v-if="v$.username.$error">
                {{ v$.username.$errors[0].$message }}
              </div>
            </div>
          </div>

          <!-- Campo Contraseña -->
          <div class="form-group">
            <div class="form-floating">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.password.$error }"
                id="password"
                v-model="formData.password"
                placeholder="Contraseña"
                required
              >
              <label for="password">
                <i class="fas fa-lock text-muted me-2"></i>Contraseña
              </label>
              <span 
                class="password-toggle"
                @click="showPassword = !showPassword"
                :title="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.password.$error">
                {{ v$.password.$errors[0].$message }}
              </div>
            </div>
          </div>

          <!-- Mensaje de error -->
          <transition name="fade">
            <div v-if="errorMessage" class="alert alert-danger">
              <i class="fas fa-exclamation-circle me-2"></i>
              {{ errorMessage }}
            </div>
          </transition>

          <!-- Registro y recuperación de contraseña links -->
          <div class="register-link mb-2">
            <p class="text-black fw-normal">
              ¿No tienes cuenta? 
              <router-link to="/crear-cuenta" class="text-primary">Regístrate aquí</router-link>
            </p>
          </div>
          <div class="register-link mb-3">
            <p class="text-black fw-normal">
              ¿Olvidaste tu contraseña?
              <router-link to="/recuperar-contrasena" class="text-primary">Recupérala aquí</router-link>
            </p>
          </div>

          <!-- Botón Submit -->
          <button
            type="submit"
            class="btn btn-primary login-button w-100 py-3"
            :disabled="isLoading"
          >
            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
            {{ isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión' }}
          </button>
        </form>
        <div v-else>
          <div class="alert alert-success text-center mb-3">
            Te enviamos un código de verificación a <strong>{{ registeredEmail }}</strong>.<br>
            Ingresa el código para activar tu cuenta.
          </div>
          <div class="form-group mb-3">
            <div class="form-floating">
              <input
                type="text"
                class="form-control text-center"
                v-model="verificationCode"
                maxlength="6"
                placeholder="Código de verificación"
              >
              <label>Código de verificación</label>
            </div>
          </div>
          <button @click="verifyCode" class="btn btn-primary w-100 mb-3" :disabled="isVerifying || verificationCode.length !== 6">
            <span v-if="isVerifying" class="spinner-border spinner-border-sm me-2"></span>
            {{ isVerifying ? 'Verificando...' : 'Verificar' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength } from '@vuelidate/validators'
import { useRouter, useRoute } from 'vue-router'
import { login } from '@/services/auth'
import { useToast } from 'vue-toastification'

const router = useRouter()
const route = useRoute()
const toast = useToast()

// Estado del formulario
const formData = reactive({
  username: '',
  password: ''
})

// Estado UI
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')

// Estado para verificación de email desde login
const showVerification = ref(false)
const verificationCode = ref('')
const isVerifying = ref(false)
const registeredEmail = ref('')

// Reglas de validación
const rules = {
  username: { required, minLength: minLength(4) },
  password: { required, minLength: minLength(6) }
}

const v$ = useVuelidate(rules, formData)

// Manejo del submit
const handleSubmit = async () => {
  try {
    errorMessage.value = '' // Limpia errores previos
    const isFormValid = await v$.value.$validate()
    
    if (!isFormValid) return
    
    isLoading.value = true
    
    // Usa la función login del servicio de autenticación
    const response = await login(formData.username, formData.password)
    
    // DEPURACIÓN EXTENDIDA
    console.log('RESPUESTA LOGIN:', {
      response_data: response,
      is_admin: response.is_admin,
      is_staff: response.is_staff
    });
    
    // Determinar correctamente el estado de administrador
    // El servidor puede devolver is_admin o is_staff (o ambos)
    const isAdminUser = response.is_admin === true || response.is_staff === true;
    
    // Guardar datos en localStorage CON CONVERSIÓN EXPLÍCITA
    localStorage.setItem('accessToken', response.access || '');
    localStorage.setItem('username', formData.username || '');
    localStorage.setItem('isAdmin', isAdminUser ? 'true' : 'false');
    localStorage.setItem('customerType', response.customer_type || 'REGULAR');
    
    console.log('ESTADO ADMIN GUARDADO:', {
      valor_evaluado: isAdminUser,
      valor_guardado: localStorage.getItem('isAdmin'),
      verificacion: localStorage.getItem('isAdmin') === 'true' ? 'CORRECTO' : 'ERROR'
    });
    
    // Si tienes más datos de usuario, guárdalos aquí
    // Lanzar evento para que Navbar se actualice
    window.dispatchEvent(new Event('storage'));
    
    // Actualizar caché de mantenimiento si el usuario es admin
    if (isAdminUser) {
      localStorage.setItem('maintenance_mode', 'false');
      const maintenanceService = (await import('@/services/maintenanceService')).default;
      await maintenanceService.resetMaintenanceCache(true);
    }
    
    // Redirigir al usuario según sus permisos y la ruta intentada
    const redirectPath = route.query.redirect || 
                        (response.is_admin ? '/configuracion/productos' : '/')
    
    router.push(redirectPath)
    
  } catch (error) {
    console.error('Error en login:', error)
    // Manejo especial para email no verificado
    const backendError = error?.response?.data?.error || error?.error;
    if (backendError === 'email_not_verified') {
      const email = error.response?.data?.email || formData.username;
      showVerification.value = true;
      registeredEmail.value = email;
      errorMessage.value = '';
      toast.info(`Te enviamos un código de verificación a ${email}`);
      return;
    }
    errorMessage.value = error.message || 'Error al iniciar sesión';
    toast.error(errorMessage.value);
  } finally {
    isLoading.value = false
  }
}

// Función para verificar el código desde login
const verifyCode = async () => {
  try {
    if (!verificationCode.value || !registeredEmail.value) {
      toast.error('El código y el email son requeridos');
      return;
    }
    isVerifying.value = true;
    await import('@/services/auth').then(({ verifyEmail }) => verifyEmail({
      email: registeredEmail.value,
      code: verificationCode.value.trim()
    }));
    toast.success('Email verificado correctamente. Ahora puedes iniciar sesión.');
    showVerification.value = false;
    verificationCode.value = '';
  } catch (error) {
    console.error('Error de verificación:', error);
    toast.error(error.message || 'Error al verificar el código');
  } finally {
    isVerifying.value = false;
  }
}

// Verificar si hay una ruta guardada al montar el componente
onMounted(() => {
  const intendedRoute = localStorage.getItem('intendedRoute')
  if (intendedRoute) {
    localStorage.removeItem('intendedRoute')
  }

  // seteo el titulo de la pagina
  // Título manejado por el router
})
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  padding: 20px;
}

.login-content {
  width: 100%;
  max-width: 450px;
}

.login-box {
  background: rgba(255, 255, 255, 0.95);
  padding: 2.5rem;
  border-radius: 15px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.login-box:hover {
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
}

.logo-container {
  text-align: center;
  margin-bottom: 2rem;
}

.logo-image {
  width: 120px;
  height: auto;
  margin-bottom: 1rem;
  animation: fadeIn 1s ease;
}

.welcome-text {
  font-size: 1.8rem;
  color: var(--black-color);
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-control {
  border: 2px solid #e9ecef;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  background-color: var(--white-color);
  color: var(--black-color);
}

.form-control:focus {
  border-color: #b6b6b6;
  background-color: var(--white-color-2);
  box-shadow: 0 0 0 0.2rem rgba(74, 144, 226, 0.25);
  color: var(--black-color);
}

.password-toggle {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #6c757d;
  transition: color 0.2s ease;
  z-index: 10;
}

.password-toggle:hover {
  color: #4a90e2;
}

.register-link {
  text-align: center;
  margin: 1rem 0;
}

.register-link a {
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s ease;
}

.register-link a:hover {
  color: #357abd !important;
}

.alert {
  border-radius: 10px;
  padding: 1rem;
}

/* Animaciones */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 576px) {
  .login-box {
    padding: 1.5rem;
  }

  .welcome-text {
    font-size: 1.5rem;
  }
}
</style>